import { useEffect, useMemo, useState } from "react";
import departmentForecastApi from "@/features/Sale/HeaderDashboard/api/departmentForecast.api";
import { DEPARTMENT_FORECAST_UPDATED_EVENT } from "@/features/Sale/HeaderDashboard/lib/departmentForecastEvents";
import { useDashboard } from "../../../lib/useDashboard";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { DEPARTMENTS_PLAN_IDS } from "../../../model/dashboard.types";
import { useDepartmentsForecastPlans } from "../DepartmentsPlan/useDepartmentsForecastPlans";

type CompanyForecastPlanResult = {
  opportunity: number | null;
  profit: number | null;
};

function sumCompanyForecastFromDepartments(
  forecasts: ReturnType<typeof useDepartmentsForecastPlans>
): CompanyForecastPlanResult {
  let opportunity = 0;
  let profit = 0;
  let hasOpportunity = false;
  let hasProfit = false;

  for (const departmentId of DEPARTMENTS_PLAN_IDS) {
    const departmentForecast = forecasts[departmentId];
    if (departmentForecast?.opportunity != null) {
      opportunity += departmentForecast.opportunity;
      hasOpportunity = true;
    }
    if (departmentForecast?.profit != null) {
      profit += departmentForecast.profit;
      hasProfit = true;
    }
  }

  return {
    opportunity: hasOpportunity ? opportunity : null,
    profit: hasProfit ? profit : null,
  };
}

export function useCompanyForecastPlan(): CompanyForecastPlanResult {
  const { dateStart, dateEnd } = useDashboard();
  const { isAuth } = useAuth();
  const departmentForecasts = useDepartmentsForecastPlans();
  const [refreshKey, setRefreshKey] = useState(0);
  const [apiForecast, setApiForecast] = useState<CompanyForecastPlanResult>({
    opportunity: null,
    profit: null,
  });

  useEffect(() => {
    const handleForecastUpdated = () => {
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener(DEPARTMENT_FORECAST_UPDATED_EVENT, handleForecastUpdated);

    return () => {
      window.removeEventListener(DEPARTMENT_FORECAST_UPDATED_EVENT, handleForecastUpdated);
    };
  }, []);

  useEffect(() => {
    if (!isAuth || !dateStart || !dateEnd) {
      setApiForecast({ opportunity: null, profit: null });
      return;
    }

    let cancelled = false;

    const loadForecast = async () => {
      try {
        const response = await departmentForecastApi.getCompanyForecastPeriodSum({
          dateStart,
          dateEnd,
        });

        if (cancelled) {
          return;
        }

        if (response.success && response.data) {
          setApiForecast({
            opportunity: response.data.turnover,
            profit: response.data.grossProfit,
          });
        } else {
          setApiForecast({ opportunity: null, profit: null });
        }
      } catch {
        if (!cancelled) {
          setApiForecast({ opportunity: null, profit: null });
        }
      }
    };

    loadForecast();

    return () => {
      cancelled = true;
    };
  }, [dateEnd, dateStart, isAuth, refreshKey]);

  const fallbackForecast = useMemo(
    () => sumCompanyForecastFromDepartments(departmentForecasts),
    [departmentForecasts]
  );

  return useMemo(
    () => ({
      opportunity: apiForecast.opportunity ?? fallbackForecast.opportunity,
      profit: apiForecast.profit ?? fallbackForecast.profit,
    }),
    [apiForecast, fallbackForecast]
  );
}
